import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CasiCloseOptions, FormModes } from '../../common/constants.lib';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogContentBase, DialogModule, DialogRef, DialogService, KENDO_DIALOGS } from '@progress/kendo-angular-dialog';
import { LoggerService } from '../../shared/logger.service';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { EditorModule } from '@progress/kendo-angular-editor';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { LabelModule } from '@progress/kendo-angular-label';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { AutoFocusDirective } from '../../directives/auto-focus.directive';
import { OrganisationType } from '../../models/models.lib';

import {
  SelectEvent,
  RemoveEvent,
  ErrorEvent,
  CancelEvent,
  PauseEvent,
  ResumeEvent,
  SuccessEvent,
  UploadEvent,
  UploadProgressEvent,
  FileInfo,
  UploadsModule,
  FileRestrictions,

} from '@progress/kendo-angular-upload'; import { SecurityService } from '../../auth/security.service';

import { KENDO_TOOLTIPS } from '@progress/kendo-angular-tooltip';
import { Settings } from '../../common/obj.lib';
import { SettingsService } from '../../settings/settings.service';
import { duplicateOrganisationValidator } from '../../common/utils.lib';
import { ErrorDialogModalComponent } from '../../common-components/error-dialog-modal/error-dialog-modal.component';
import { CasiKendoEditorComponent } from '../../common-components/casi-kendo-editor/casi-kendo-editor.component';

@Component({
  selector: 'app-organisation-edit-win',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    DialogModule,
    EditorModule,
    InputsModule,
    ButtonsModule,
    LabelModule,
    DropDownsModule,
    LayoutModule,
    AutoFocusDirective,
    UploadsModule,
    CasiKendoEditorComponent,
    KENDO_TOOLTIPS,
    KENDO_DIALOGS
  ],
  templateUrl: './organisation-edit-win.html',
  styleUrl: './organisation-edit-win.scss'
})
export class OrganisationEditWin implements OnInit {
  private _organisation: OrganisationType | undefined;

  public studentEmailTemplate = '';
  public supervisorEmailTemplate = '';

  @Input() title: string = 'Title Not Set';

  @Input({ required: true }) existingOrganisations: OrganisationType[] = [];

  @Input({ required: true }) set organisation(val: OrganisationType) {
    this._organisation = val;
    this.editForm.patchValue(this._organisation);
    this.studentEmailTemplate = (this._organisation.student_email_template as string);
    this.supervisorEmailTemplate = (this._organisation.supervisor_email_template as string);
  }
  get organisation(): OrganisationType | undefined {
    return this._organisation;
  };

  private _mode: FormModes = FormModes.NOTSET;
  @Input({ required: true }) set mode(val: FormModes) {
    this._mode = val;
  }
  get mode(): FormModes {
    return this._mode;
  };

  @Output() onSubmit: EventEmitter<FormGroup> = new EventEmitter();
  @Output() onClose: EventEmitter<void> = new EventEmitter();

  editForm: FormGroup;
  // public uploadSaveUrl: string = "organisationLogoUpload";
  // public uploadRemoveUrl: string = 'logoRemove';
  // public hasLogo: boolean = true;
  // public logo: string | null | undefined;
  // public myRestrictions: FileRestrictions = {
  //   allowedExtensions: ["jpg", "jpeg", "png"],
  // };
  //
  // private settings: Settings = this.settingsService.getSettings();

  constructor(
    private fb: FormBuilder,
    private logger: LoggerService,
    private securityService: SecurityService,
    private settingsService: SettingsService,
  ) {
    this.editForm = this.fb.group(
      {
        id: [null, []],
        organisation_name: [null, [Validators.required]],
        business_owner_name: [null, [Validators.required]],
        contact_email: [null, [Validators.required, Validators.email]],
        contact_telephone: [null, [Validators.required, Validators.pattern(/^[0-9+\-()\s]{7,20}$/)]], //This allows: +1 234 567 890 or (012) 345-6789 or 0123456789
        address: [null, []],
        logo: [null, []],
        student_email_template: [null, [Validators.required]],
        supervisor_email_template: [null, [Validators.required]]
      }
    );
  }

  //#region Form

  get f() { return this.editForm.controls; }
  get organisation_name() { return this.editForm.get('organisation_name'); }
  get business_owner_name() { return this.editForm.get('business_owner_name'); }
  get contact_email() { return this.editForm.get('contact_email'); }
  get contact_telephone() { return this.editForm.get('contact_telephone'); }
  get address() { return this.editForm.get('address'); }
  get student_email_template() { return this.editForm.get('student_email_template'); }
  get supervisor_email_template() { return this.editForm.get('supervisor_email_template'); }

  // get logo() {
  //   return this.editForm.get('logo');
  // }
  //#endregion

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    this.editForm.setValidators(
      duplicateOrganisationValidator(this.existingOrganisations, this.mode)
    );

    this.contact_email?.valueChanges.subscribe(value => {
      if (value && value !== value.toLowerCase()) {
        this.contact_email?.setValue(value.toLowerCase(), { emitEvent: false });
      }
    });

    // this.uploadSaveUrl = `${this.settings.apiUrl}/organisationLogoUpload`;
    // this.uploadRemoveUrl = `${this.settings.apiUrl}/logoRemove`; //not implemented
    // this.logo  = this.organisation?.logo || null;
    // this.hasLogo = this.logo ? true :  false;
  }

  public onCancelAction(): void {
    this.logger.debug(`onCancelAction...`);
    this.onClose.emit();
  }

  public onSaveAction(): void {
    this.logger.debug(`onSaveAction...`, this.editForm.value);

    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }
    this.onSubmit.emit(this.editForm);
  }


  public studentEmailTemplateChange(value: any): void {
    this.logger.debug(`studentEmailTemplateChange...`, value);
    this.editForm.patchValue({ student_email_template: value });
  }

  public supervisorEmailTemplateChange(value: any): void {
    this.logger.debug(`supervisorEmailTemplateChange...`, value);
    this.editForm.patchValue({ supervisor_email_template: value });
  }

  //#region  Logo

  // public cancel(e: CancelEvent): void {
  //   this.logger.debug(`cancel event...`, e);
  // }
  //
  // public clear(): void {
  //   this.logger.debug("clear event");
  // }
  //
  // public complete(): void {
  //   this.logger.debug(`complete event`);
  // }
  //
  // public error(e: ErrorEvent): void {
  //   this.logger.debug(`error event: ${e.files[0].name}`);
  // }
  //
  // public blur(): void {
  //   this.logger.debug(`blur event`);
  // }
  //
  // public focus(): void {
  //   this.logger.debug(`focus event`);
  // }
  //
  // public pause(e: PauseEvent): void {
  //   this.logger.debug(`pause event ${e.file.name}`);
  // }
  //
  // public resume(e: ResumeEvent): void {
  //   this.logger.debug(`resume event ${e.file.name}`);
  // }
  //
  // public remove(e: RemoveEvent): void {
  //   this.logger.debug(`remove event: ${e.files[0].name}`);
  //   // this.clearLogo('business');
  // }
  //
  // public select(e: SelectEvent): void {
  //   this.logger.debug(`select event`, e);
  // }
  //
  // public success(e: SuccessEvent): void {
  //   this.logger.debug(`successBusiness event:`, e);
  //   if (e.files && e.operation === "upload") {
  //     let name = e.files[0].name;
  //     let img = (e.response as any).body.img;
  //     this.logger.debug(`success event ${name}`, e);
  //     // this.logger.debug(`success:img`, img);
  //     this.editForm.patchValue({ logo: img })
  //     this.logo = img;
  //     this.hasLogo = true;
  //     this.toggleDirty();
  //   } else {
  //     // this.clearLogo('business');
  //   }
  // }
  //
  // public upload(e: UploadEvent): void {
  //   this.logger.debug(`upload event ${e.files[0].name}`);
  //   this.securityService.secureUploads(e, 'Logo uploading');
  // }
  //
  // public uploadProgress(e: UploadProgressEvent): void {
  //   this.logger.debug(`uploadProgress event ${e.percentComplete}`);
  // }
  //
  // public valueChange(e: FileInfo[]): void {
  //   this.logger.debug(`valueChange:e: `, e);
  //   // this.logger.debug(`valueChange event ${e[0].name}`);
  // }

  //#endregion

  // private toggleDirty() {
  //   this.editForm.markAsDirty();
  // }
  //
  // clearLogo() {
  //   this.logger.debug(`clearLogo: `);
  //   this.editForm.patchValue({ param_primary_image: null });
  //   this.logo = null;
  //   this.hasLogo = false;
  //   this.toggleDirty();
  // }
}

